import { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { api, errorMessage } from "../../lib/api";
import { Markdown } from "../../lib/markdown";
import { toast } from "sonner";

const CATEGORIES = [
    { slug: "getting-started-members", label: "Getting started — Members" },
    { slug: "getting-started-advertisers", label: "Getting started — Advertisers" },
    { slug: "payments-and-cashout", label: "Payments & cashout" },
    { slug: "trust-and-safety", label: "Trust & safety" },
    { slug: "privacy-and-data", label: "Privacy & data" },
    { slug: "troubleshooting", label: "Troubleshooting" },
];

const EMPTY = {
    id: null,
    title: "",
    slug: "",
    category: "getting-started-members",
    body: "",
    excerpt: "",
    status: "draft",
};

export default function AdminKB() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [editor, setEditor] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(false);

    const load = () => api.get("/kb/admin/articles").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (filter === "all") return items;
        return items.filter((it) => it.status === filter);
    }, [items, filter]);

    const startNew = () => { setEditor(EMPTY); setPreview(false); };
    const startEdit = async (it) => {
        try {
            const r = await api.get(`/kb/admin/articles/${it.id}`);
            const full = r.data;
            setEditor({
                id: full.id,
                title: full.title,
                slug: full.slug,
                category: full.category,
                body: full.body || "",
                excerpt: full.excerpt || "",
                status: full.status,
            });
            setPreview(false);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    const save = async () => {
        if (editor.title.trim().length < 3) return toast.error("Title must be at least 3 characters");
        if (editor.body.trim().length < 10) return toast.error("Body must be at least 10 characters");
        setBusy(true);
        try {
            if (editor.id) {
                await api.patch(`/kb/admin/articles/${editor.id}`, {
                    title: editor.title,
                    slug: editor.slug || undefined,
                    category: editor.category,
                    body: editor.body,
                    excerpt: editor.excerpt || undefined,
                    status: editor.status,
                });
                toast.success("Saved");
            } else {
                await api.post("/kb/admin/articles", {
                    title: editor.title,
                    slug: editor.slug || undefined,
                    category: editor.category,
                    body: editor.body,
                    excerpt: editor.excerpt || undefined,
                    status: editor.status,
                });
                toast.success("Created");
                setEditor(EMPTY);
            }
            await load();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (it) => {
        if (!window.confirm(`Delete "${it.title}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/kb/admin/articles/${it.id}`);
            toast.success("Deleted");
            if (editor.id === it.id) setEditor(EMPTY);
            await load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <section className="max-w-6xl mx-auto px-6 py-16" data-testid="admin-kb">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <p className="eyebrow mb-2">KNOWLEDGE BASE</p>
                        <h1 className="h1">Manage articles</h1>
                    </div>
                    <button onClick={startNew} className="btn btn-primary" data-testid="admin-kb-new">+ New article</button>
                </div>

                <div className="grid lg:grid-cols-12 gap-8">
                    {/* List */}
                    <aside className="lg:col-span-4">
                        <div className="flex gap-2 mb-3 font-mono text-xs">
                            {[["all", "All"], ["published", "Published"], ["draft", "Draft"]].map(([k, label]) => (
                                <button
                                    key={k}
                                    onClick={() => setFilter(k)}
                                    className={`px-3 py-1 border ${filter === k ? "bg-ink text-paper border-ink" : "border-ink-300 hover:border-ink"}`}
                                    data-testid={`admin-kb-filter-${k}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <ul className="border border-ink-200 divide-y divide-ink-200" data-testid="admin-kb-list">
                            {filtered.map((it) => (
                                <li
                                    key={it.id}
                                    className={`p-3 cursor-pointer hover:bg-ink-50 ${editor.id === it.id ? "bg-ink-50" : ""}`}
                                    onClick={() => startEdit(it)}
                                    data-testid={`admin-kb-row-${it.slug}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold truncate">{it.title}</div>
                                            <div className="text-[10px] font-mono text-ink-600 mt-1">
                                                {it.category} · {it.view_count || 0} views
                                            </div>
                                        </div>
                                        <span className={`font-mono text-[10px] px-2 py-0.5 border ${it.status === "published" ? "border-success text-success" : "border-ink-300 text-ink-600"}`}>
                                            {it.status}
                                        </span>
                                    </div>
                                </li>
                            ))}
                            {filtered.length === 0 && (
                                <li className="p-4 text-sm text-ink-600">No articles match the current filter.</li>
                            )}
                        </ul>
                    </aside>

                    {/* Editor */}
                    <div className="lg:col-span-8" data-testid="admin-kb-editor">
                        <div className="border border-ink-200 p-6 bg-paper">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold">
                                    {editor.id ? "Edit article" : "New article"}
                                </h2>
                                <button
                                    type="button"
                                    onClick={() => setPreview((v) => !v)}
                                    className="font-mono text-xs underline"
                                    data-testid="admin-kb-preview-toggle"
                                >
                                    {preview ? "edit" : "preview"}
                                </button>
                            </div>

                            {!preview ? (
                                <div className="space-y-3">
                                    <Field label="Title">
                                        <input
                                            className="input-base w-full"
                                            value={editor.title}
                                            onChange={(e) => setEditor((s) => ({ ...s, title: e.target.value }))}
                                            data-testid="admin-kb-title"
                                        />
                                    </Field>
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <Field label="Slug (optional — auto-generated)">
                                            <input
                                                className="input-base w-full"
                                                placeholder="auto from title"
                                                value={editor.slug}
                                                onChange={(e) => setEditor((s) => ({ ...s, slug: e.target.value }))}
                                                data-testid="admin-kb-slug"
                                            />
                                        </Field>
                                        <Field label="Category">
                                            <select
                                                className="input-base w-full"
                                                value={editor.category}
                                                onChange={(e) => setEditor((s) => ({ ...s, category: e.target.value }))}
                                                data-testid="admin-kb-category"
                                            >
                                                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                                            </select>
                                        </Field>
                                    </div>
                                    <Field label="Excerpt (optional — auto-generated from body)">
                                        <textarea
                                            className="input-base w-full"
                                            rows={2}
                                            value={editor.excerpt}
                                            onChange={(e) => setEditor((s) => ({ ...s, excerpt: e.target.value }))}
                                            data-testid="admin-kb-excerpt"
                                        />
                                    </Field>
                                    <Field label="Body (Markdown)">
                                        <textarea
                                            className="input-base w-full font-mono text-sm"
                                            rows={16}
                                            value={editor.body}
                                            onChange={(e) => setEditor((s) => ({ ...s, body: e.target.value }))}
                                            placeholder="## Heading&#10;Paragraph text **bold** *italic* `code`&#10;- list item"
                                            data-testid="admin-kb-body"
                                        />
                                    </Field>
                                    <div className="flex items-center justify-between mt-5">
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={editor.status === "published"}
                                                onChange={(e) => setEditor((s) => ({ ...s, status: e.target.checked ? "published" : "draft" }))}
                                                data-testid="admin-kb-published"
                                            />
                                            Published
                                        </label>
                                        <div className="flex gap-2">
                                            {editor.id && (
                                                <button
                                                    type="button"
                                                    onClick={() => remove(items.find((x) => x.id === editor.id))}
                                                    className="btn btn-ghost text-error border border-error"
                                                    data-testid="admin-kb-delete"
                                                >
                                                    Delete
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={save}
                                                disabled={busy}
                                                className="btn btn-primary"
                                                data-testid="admin-kb-save"
                                            >
                                                {busy ? "Saving…" : editor.id ? "Save changes" : "Create article"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div data-testid="admin-kb-preview">
                                    <h2 className="text-2xl font-semibold mb-4">{editor.title || "Untitled"}</h2>
                                    {editor.excerpt && (
                                        <p className="text-ink-600 italic border-l-2 border-ink-300 pl-4 mb-6">{editor.excerpt}</p>
                                    )}
                                    <Markdown source={editor.body || "_(empty body)_"} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider text-ink-600 mb-1 block">{label}</span>
            {children}
        </label>
    );
}
